import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { track } from "../../../analytics";
import './HomeGeneralSlider.scss';
import { homeSlider } from '../../../Modules/HomeModule/HomeLogic/HomeLogic';
import { useTheme } from '../../../theme/ThemeProvider';
import { readCache, writeCache } from '../../../Modules/GeneralModule/swrCache';
import Form from '../../General/Form/Form';
import { setHeroHeight, readHeroHeight } from './heroHeight';

const SLIDE_DURATION = 5000;
// Слайды кэшируем на сутки: при недоступном API показываем последние
// известные вместо пустого места на первом экране.
const SLIDES_CACHE_KEY = 'nets_slides_cache';
// Дольше ждать нечего: пустая коробка на первом экране хуже, чем секция,
// которой просто нет.
const REQUEST_TIMEOUT = 8000;
// На мобильных (≤767px) слайдер превращается в экран-«сторис» на всю высоту
// первого экрана: слайды стопкой, кроссфейд, полоса миниатюр внизу.
const MOBILE_QUERY = '(max-width: 767px)';
// Свайп по сторис: 40px по горизонтали — смена слайда. Движение меньше 8px
// свайпом не считается, это удержание (пауза автоплея).
const SWIPE_THRESHOLD = 40;
const HOLD_THRESHOLD = 8;
// У слайдов из API есть id, у хардкодных наборов (videoData на /video) — нет,
// и key={s.id} давал undefined на весь список. Ключ строим из того, что есть.
const slideKey = (s, i) => (s?.id ?? `${i}-${s?.title ?? ''}`);
/*
  Запасной слайд: API не ответил и кэша нет. Экран всё равно держит
  высоту, поэтому вместо пустоты — один статичный кадр со ссылкой на
  тарифы.
*/
const FALLBACK_TITLE = 'Интернет и IPTV от NETS';
const FALLBACK_HREF = '#tarifs';
/*
  Стык героя и листа. Те же числа, что в styles/mixins/_hero.scss:
  лист наезжает на герой ровно на SHEET_OVERLAP, а SHEET_CLEARANCE —
  свободная полоса между низом панели и кромкой листа. Здесь они нужны
  только для проверки инвариантов в dev-сборке.
*/
/*
  Отладочная плашка героя: ?hero-debug=1. Ленивый import — отдельный чанк,
  в обычный бандл попадает только проверка параметра.
*/
const HeroDebug = lazy(() => import('./HeroDebug'));
const heroDebugOn = () => {
  try {
    return new URLSearchParams(window.location.search).get('hero-debug') === '1';
  } catch (e) {
    return false;
  }
};

/*
  Когда пересчитывать --hero-h. Сворачивание адресной строки при
  прокрутке меняет innerHeight на 60–100px, и если реагировать на
  каждый resize, герой дышит прямо под пальцем, а лист за ним дёргается.
  Поэтому пересчитываем только в двух случаях: человек наверху (рывка не
  видно) или высота прыгнула так, что это точно поворот экрана.
*/
const HERO_H_TOP_GUARD = 8;
const HERO_H_BIG_JUMP = 150;
// На монтировании поправляем даже мелкое расхождение — это ещё до краски.
const HERO_H_MOUNT_EPS = 2;

const SHEET_OVERLAP = 32;
const SHEET_CLEARANCE = 12;
const HERO_MIN_HEIGHT = 600;
// Короткий заголовок набираем крупнее: на экране он смотрится как
// афиша, а не как строчка текста. Порог подобран по данным — длиннее
// 22 символов на 390 уже уходит в три строки.
const BIG_TITLE_MAX = 22;
const isBigTitle = (t) => String(t || '').trim().length <= BIG_TITLE_MAX;
/*
  Необязательные поля слайда под тёмную тему: image_dark, image_item_dark,
  text_color_dark — своя версия фона, предмета и цвета текста, и
  overlay_dark — сила затемнения в процентах (0–60). Любое может быть null
  или вовсе отсутствовать (старый nets_slides_cache) — тогда берём светлое.
  В светлой теме тёмные поля игнорируются.

  Рисуем ТОЛЬКО набор активной темы: один src на слайд, при смене темы он
  меняется. Второй комплект не монтируем — это лишний фон и лишний предмет
  на каждый слайд впустую.
*/
const OVERLAY_DARK_MAX = 60;
const slideImage = (s, dark) => (dark && s.image_dark) || s.image;
const slideItem = (s, dark) => (dark && s.image_item_dark) || s.image_item;
/*
  Предмет на мобильном экране: у портретного кадра своя композиция, поэтому
  бэк может прислать отдельный image_item_mobile. Поля может не быть вовсе
  (и сейчас нет) — тогда берётся обычный предмет по теме. Тёмного варианта
  мобильного предмета в схеме нет: если он понадобится, это новое поле.
*/
const slideItemMobile = (s, dark) => s.image_item_mobile || slideItem(s, dark);
const slideTextColor = (s, dark) =>
  (dark && s.text_color_dark) || s.text_color || 'var(--color-inverse)';
/*
  У слайда есть своя тёмная картинка — она уже нарисована под тёмную тему.
  Приглушать её нечем: ни фильтра, ни виньетки, ни overlay. Признак
  вешается на слой, а не на контейнер: рядом (в стопке на мобиле, в
  кроссфейде на десктопе) соседние слайды могут быть разными.
*/
const hasNativeDark = (s, dark) => Boolean(dark && s.image_dark);
const darkOverlay = (s, dark) => {
  if (!dark || hasNativeDark(s, dark)) return 0;
  const v = Number(s.overlay_dark);
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.min(v, OVERLAY_DARK_MAX) / 100;
};

// Поля позиции в данных нет — дефолт center, исключения по id
// (широкоформатные баннеры: держим ключевой объект в кадре портрета).
const BG_POSITION = {
  18: 'right center', // «Высокая скорость» — кабели у правого края баннера
  20: 'right center', // Навруз — орнамент смещён вправо
};

/*
  hero — только для главной: там слайдер занимает весь первый экран
  от самого верха, прилипает под листом .page-sheet и забирает шапку
  в прозрачный режим. На остальных страницах (/business, /hosting,
  /router, /sks, /video) слайдер остаётся обычным блоком в потоке:
  листа там нет, и прилипший герой просвечивал бы сквозь секции ниже.
*/
const HomeGeneralSlider = ({ data, black, hero }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Три состояния вместо двух: ждём, показали, не смогли.
  const [slides, setSlides] = useState(() => data || readCache(SLIDES_CACHE_KEY) || []);
  const [status, setStatus] = useState(() =>
    data || readCache(SLIDES_CACHE_KEY) ? 'ready' : 'loading'
  );
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );
  /*
    Слайдер на мобиле остаётся только на главной. На остальных страницах
    (/business, /hosting, /router, /system, /video) он там ничего не
    добавлял: тот же набор слайдов, что и на главной, занимал первый
    экран целиком и отодвигал содержимое страницы. Прячем целиком — без
    разметки, без резерва высоты, без автоплея и без запроса слайдов.
  */
  const hiddenOnMobile = isMobile && !hero;

  // Таймер читает точку перелома через ref: пересоздавать его на смене
  // вёрстки не нужно, а setInterval замыкает значение на момент старта.
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const elapsedRef = useRef(0);
  // Мобильные сторис
  const storiesRef = useRef(null);
  const panelRef = useRef(null);
  const pointerRef = useRef(null);
  // Ключ прогона прогресса: меняется на КАЖДЫЙ переход, в том числе на тап
  // по уже активной миниатюре. Служит React-ключом элемента прогресса —
  // элемент пересоздаётся, и CSS-анимация стартует с нуля.
  const [runKey, setRunKey] = useState(0);
  // Секция вне вьюпорта или вкладка скрыта — автоплей не крутим.
  const [isVisible, setIsVisible] = useState(true);

  const getHomeSlider = () => {
    let settled = false;
    // Если данные уже есть из кэша — ошибка их не роняет.
    const fail = () => setStatus(slidesRef.current.length ? 'ready' : 'error');
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      fail();
    }, REQUEST_TIMEOUT);

    homeSlider()
      .then((res) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        const next = Array.isArray(res.data) ? [...res.data].reverse() : [];
        if (!next.length) return fail();
        setSlides(next);
        writeCache(SLIDES_CACHE_KEY, next);
        setStatus('ready');
      })
      .catch((err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        console.log(err);
        fail();
      });
  };

  useEffect(() => {
    // на мобиле вне главной слайдера нет — и запроса тоже
    if (hiddenOnMobile) return;
    if (data) {
      setSlides(data);
      setStatus('ready');
      return;
    }
    getHomeSlider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenOnMobile]);

  // Слежение за точкой перелома: на мобиле рендерится карточная лента,
  // JS-логика слайдера (автоплей/стрелки) не работает вовсе.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // старые Safari
    // страховка: в некоторых встраиваемых движках change не стреляет
    window.addEventListener('resize', onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);

  const startTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    startTimeRef.current = Date.now() - elapsedRef.current;

    /*
      Один таймер на обе вёрстки. На десктопе он же двигает полоски
      прогресса через состояние (30мс), на мобиле прогресс рисует CSS-
      анимация на миниатюре — состояние там не нужно, и тик реже, чтобы
      не перерисовывать экран 33 раза в секунду на телефоне.
    */
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      if (!isMobileRef.current) {
        setProgress(Math.min((elapsed / SLIDE_DURATION) * 100, 100));
      }
      if (elapsed >= SLIDE_DURATION) {
        elapsedRef.current = 0;
        setCurrentIndex(prev => {
          setPrevIndex(prev);
          return (prev + 1) % slides.length;
        });
        setRunKey((k) => k + 1);
        setProgress(0);
        startTimeRef.current = Date.now();
      }
    }, isMobileRef.current ? 250 : 30);
  }, [slides.length]);

  useEffect(() => {
    if (!slides.length) return undefined;
    if (!isPaused && isVisible) startTimer();
    return () => clearInterval(intervalRef.current);
  }, [slides.length, isMobile, isPaused, isVisible, startTimer]);

  /*
    Один обработчик прокрутки на две вещи.

    1. Автоплей не крутится, пока сторис не видно: скрытая вкладка и момент,
       когда лист страницы наехал на герой (его верх дошёл до верха экрана).
       Иначе слайды мотаются вхолостую, пока человек читает тарифы ниже.

    2. Пока герой — это то, что лежит под шапкой, шапка прозрачная.

    Сентинела в начале героя тут быть не может, хотя так и просится:
    герой на главной sticky, и 1px-элемент внутри него остаётся на y=0 при
    любой прокрутке (замерено: на scrollY 900 верх героя всё ещё 0), то есть
    IntersectionObserver никогда не погаснет. Единственное, что реально
    едет, — лист страницы, по нему и считаем.

    Лист ищем по классу: он лежит в разметке страницы, а не слайдера. Если
    листа нет (слайдер на /business и прочих), считаем по самому слайдеру —
    он там обычный блок и из вьюпорта уходит.
  */
  useEffect(() => {
    // Без слайдов пауза бессмысленна, но прозрачный режим шапки нужен и
    // над скелетоном, и над запасным слайдом — герой там тот же.
    if (!isMobile || hiddenOnMobile) return undefined;
    const root = document.documentElement;
    let frame = 0;
    const update = () => {
      frame = 0;
      const sheet = document.querySelector('.page-sheet');
      const el = storiesRef.current;
      const sheetTop = sheet ? sheet.getBoundingClientRect().top : null;
      // Пауза — только когда есть что паузить: у скелетона и запасного
      // слайда своей секции нет, а шапку над ними красить всё равно надо.
      if (el) {
        const covered = sheetTop !== null
          ? sheetTop <= 0
          : el.getBoundingClientRect().bottom <= 0;
        setIsVisible(!document.hidden && !covered);
      }

      if (!hero) return;
      const bar = document.querySelector('.mmenu__bar');
      const barH = bar ? bar.getBoundingClientRect().height : 0;
      const overHero = sheetTop === null || sheetTop > barH;
      if (overHero) root.setAttribute('data-over-hero', '');
      else root.removeAttribute('data-over-hero');
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };
    update();
    document.addEventListener('visibilitychange', update);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
      root.removeAttribute('data-over-hero');
    };
  }, [isMobile, slides.length, hero, hiddenOnMobile]);

  /*
    --hgs-panel-h — сколько низа героя занято панелью вместе с резервом
    под кромку листа. Слайд лежит под панелью целиком (фон должен
    доходить до низа экрана), поэтому строку сцены надо чем-то
    ограничить снизу. Меряем от низа героя до верха панели, а не высоту
    самой панели: резерв строки сетки тогда учитывается сам собой.
    Считать это в CSS нечем — панель набирается из кнопки, миниатюр,
    точек и безопасной зоны.
  */
  useEffect(() => {
    if (!isMobile || !slides.length) return undefined;
    const panel = panelRef.current;
    const host = storiesRef.current;
    if (!panel || !host) return undefined;
    const apply = () => {
      const h = Math.round(
        host.getBoundingClientRect().bottom - panel.getBoundingClientRect().top
      );
      host.style.setProperty('--hgs-panel-h', `${h}px`);
    };
    apply();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [isMobile, slides.length]);

  /*
    Страховка для разработки: стык героя и листа легко развалить
    правкой в соседнем файле, а на глаз сдвиг в десяток пикселей не
    виден. Проверяем инварианты после монтирования и на resize и, если
    что-то разошлось, пишем в консоль с цифрами. В прод-сборке блок
    вырезается сборщиком целиком.
  */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    if (!isMobile || !hero) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      /*
        Только в самом верху страницы. Герой прилипший, и его rect
        считается по прилипшему положению: на прокрутке «нахлёст» выходит
        равным прокрутке плюс 32 и проверка начинает врать (замерено: 199
        при автопрокрутке страницы на 167).
      */
      if (window.scrollY > 0) return;
      const root = document.querySelector('.hgs-root--hero');
      const sheet = document.querySelector('.page-sheet');
      const panel = document.querySelector('.hgs-panel, .hgs-heroskel__panel');
      if (!root || !sheet) return;
      const r = root.getBoundingClientRect();
      const sh = sheet.getBoundingClientRect();
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:100svh;visibility:hidden;pointer-events:none';
      document.body.appendChild(probe);
      const svh = probe.getBoundingClientRect().height;
      probe.remove();
      // сверяем с innerHeight, а не с svh: высота героя берётся из него
      const want = Math.max(HERO_MIN_HEIGHT, window.innerHeight);
      const bad = [];
      if (Math.abs(r.height - want) > 1) bad.push(`высота героя ${Math.round(r.height)} вместо ${Math.round(want)}`);
      if (Math.abs(r.bottom - SHEET_OVERLAP - sh.top) > 1) {
        bad.push(`нахлёст ${Math.round(r.bottom - sh.top)} вместо ${SHEET_OVERLAP}`);
      }
      if (panel) {
        const pb = panel.getBoundingClientRect().bottom;
        if (pb > sh.top - SHEET_CLEARANCE + 1) {
          bad.push(`низ панели ${Math.round(pb)} ниже кромки листа минус зазор ${Math.round(sh.top - SHEET_CLEARANCE)}`);
        }
      }
      if (bad.length) {
        console.warn('[hero] invariant', {
          проблемы: bad,
          innerHeight: window.innerHeight,
          svh: Math.round(svh),
          heroH: document.documentElement.style.getPropertyValue('--hero-h'),
          герой: { top: Math.round(r.top), bottom: Math.round(r.bottom), height: Math.round(r.height) },
          лист: { top: Math.round(sh.top), marginTop: getComputedStyle(sheet).marginTop },
          панель: panel ? { top: Math.round(panel.getBoundingClientRect().top), bottom: Math.round(panel.getBoundingClientRect().bottom) } : null,
        });
      }
    };
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(check);
    };
    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isMobile, hero, status, slides.length]);

  /*
    Перечитывание высоты на монтировании, до первой отрисовки.

    Head-скрипт ставит --hero-h в момент разбора <head>, но размер
    вьюпорта к этому моменту бывает ещё не тот: в мобильной эмуляции
    Edge и Chrome страница сначала размечается по окну девтулзов и
    только потом получает заданные 390x844. Поэтому на первом коммите
    перечитываем innerHeight и, если он разошёлся больше чем на 2px,
    выставляем заново — useLayoutEffect успевает до краски.

    Условие по scrollY то же, что у resize: правим только когда человек
    наверху и рывка не видно.
  */
  useLayoutEffect(() => {
    if (!hero || !isMobile) return;
    const current = readHeroHeight();
    const next = window.innerHeight;
    const off = current === null || Math.abs(next - current) > HERO_H_MOUNT_EPS;
    if (off && window.scrollY <= HERO_H_TOP_GUARD) setHeroHeight('mount');
  }, [hero, isMobile]);

  /*
    Дальше высоту двигают только resize и поворот, с дебаунсом через
    rAF. На load ничего не пересчитываем: load — не причина менять
    высоту, а лишний пересчёт после первой отрисовки это как раз
    видимый прыжок.
  */
  useEffect(() => {
    if (!hero || !isMobile) return undefined;
    let frame = 0;
    const apply = (source) => {
      frame = 0;
      const next = window.innerHeight;
      const current = readHeroHeight() || 0;
      const delta = Math.abs(next - current);
      if (delta < 1) return;
      const atTop = window.scrollY <= HERO_H_TOP_GUARD;
      const rotated = delta >= HERO_H_BIG_JUMP;
      if (!atTop && !rotated) return;
      setHeroHeight(source);
    };
    const schedule = (source) => () => {
      if (frame) return;
      frame = requestAnimationFrame(() => apply(source));
    };
    const onResize = schedule('resize');
    const onOrientation = schedule('orientationchange');
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientation);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientation);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [hero, isMobile]);

  const goToSlide = (index) => {
    clearInterval(intervalRef.current);
    elapsedRef.current = 0;
    setPrevIndex(currentIndex);
    setCurrentIndex(index);
    setRunKey((k) => k + 1); // прогресс миниатюры стартует заново
    setProgress(0);
    if (!isPaused && isVisible) startTimer();
  };

  const debug = hero && isMobile && heroDebugOn() ? (
    <Suspense fallback={null}>
      <HeroDebug />
    </Suspense>
  ) : null;

  const nextSlide = () => goToSlide((currentIndex + 1) % slides.length);
  const prevSlide = () => goToSlide((currentIndex - 1 + slides.length) % slides.length);

  // Ничего не рисуем: ни разметки, ни резерва высоты — первая секция
  // страницы встаёт сразу под шапкой на свой собственный отступ.
  if (hiddenOnMobile) return null;

  if (!slides.length) {
    /*
      Hero-режим: высота героя одна и та же при любом состоянии данных.
      Схлопнуть его нельзя — лист идёт с отрицательным margin и прыгнул бы
      к шапке, а потом обратно, когда слайды доедут.
    */
    if (isMobile && hero) {
      if (status === 'loading') {
        return (
          <div className="hgs-root hgs-root--stories hgs-root--hero hgs-root--reserved" aria-hidden="true">
            <div className="hgs-heroskel">
              <div className="hgs-heroskel__text">
                <span className="hgs-heroskel__bar hgs-heroskel__bar--title" />
                <span className="hgs-heroskel__bar hgs-heroskel__bar--title2" />
                <span className="hgs-heroskel__bar hgs-heroskel__bar--desc" />
              </div>
              <div className="hgs-heroskel__panel">
                <span className="hgs-heroskel__bar hgs-heroskel__bar--btn" />
              </div>
            </div>
            {debug}
          </div>
        );
      }
      /*
        Данных нет и кэша нет. Пустой экран на всю высоту был бы хуже
        дырки, поэтому показываем статичный слайд из кода: он ничего не
        знает про API и ведёт к тарифам.
      */
      return (
        <div className="hgs-root hgs-root--stories hgs-root--hero">
          <section className="hgs-stories hgs-stories--fallback">
            <div className="hgs-stack">
              <article className="hgs-story is-active">
                <div className="hgs-story__text">
                  <h1 className={"hgs-story__title" + (isBigTitle(FALLBACK_TITLE) ? " hgs-story__title--big" : "")}>
                    {FALLBACK_TITLE}
                  </h1>
                </div>
                <div className="hgs-story__stage" />
              </article>
            </div>
            <div className="hgs-panel">
              <div className="hgs-panel__cta">
                <a className="hgs-panel__btn" href={FALLBACK_HREF}>К тарифам</a>
              </div>
            </div>
          </section>
          {debug}
        </div>
      );
    }
    // Пока ждём — держим место той же высоты, но не пустой дырой: плитка
    // с шиммером, как у тарифов.
    if (status === 'loading') {
      return (
        /*
          На мобиле заглушка держит не только высоту, но и геометрию стыка:
          тот же сдвиг под шапку и тот же нахлёст листа тарифов. Без класса
          --stories тарифы стояли на 14px ниже, чем после прихода данных.
        */
        <div
          className={"hgs-root hgs-root--reserved" + (isMobile ? " hgs-root--stories" : "")}
          aria-hidden="true"
        >
          <div className="hgs-skeleton" />
        </div>
      );
    }
    // Ошибка или пустой ответ: секции просто нет. Один сдвиг в этот момент
    // лучше, чем пустое место на первом экране.
    return null;
  }

  const slide = slides[currentIndex];

  /*
    Мобильный экран (<=767px) — «сторис» на всю высоту первого экрана.
    Слайды лежат стопкой, активный проявляется кроссфейдом; индекс и
    автоплей те же, что у десктопа, второго слайдера нет.
  */
  if (isMobile) {
    const pause = () => {
      if (isPaused) return;
      setIsPaused(true);
      clearInterval(intervalRef.current);
      elapsedRef.current = Date.now() - startTimeRef.current;
    };
    const resume = () => setIsPaused(false);

    // Свайп и удержание на одном указателе: пока палец не сдвинулся дальше
    // HOLD_THRESHOLD — это удержание (пауза), дальше — жест смены слайда.
    const onPointerDown = (e) => {
      pointerRef.current = { x: e.clientX, y: e.clientY, moved: false };
      pause();
    };
    const onPointerMove = (e) => {
      const p = pointerRef.current;
      if (!p) return;
      if (Math.abs(e.clientX - p.x) > HOLD_THRESHOLD) p.moved = true;
    };
    const onPointerUp = (e) => {
      const p = pointerRef.current;
      pointerRef.current = null;
      if (!p) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      // вертикаль не перехватываем: это прокрутка страницы
      if (p.moved && Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        const n = slides.length;
        goToSlide(dx < 0 ? (currentIndex + 1) % n : (currentIndex - 1 + n) % n);
      }
      resume();
    };
    const onPointerCancel = () => { pointerRef.current = null; resume(); };

    return (
      <div className={"hgs-root hgs-root--stories" + (hero ? " hgs-root--hero" : "")}>
        <section
          className="hgs-stories"
          ref={storiesRef}
          style={{ "--hgs-interval": `${SLIDE_DURATION}ms` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="hgs-stack">
            {slides.map((s, i) => {
              const dim = darkOverlay(s, isDark);
              const item = slideItemMobile(s, isDark);
              const active = i === currentIndex;
              // Все пять слайдов смонтированы, поэтому h1 — только у
              // активного: пять h1 на странице были бы и для поиска, и для
              // скринридера пятью разными главными заголовками. Неактивные
              // вдобавок aria-hidden, так что до озвучки не доходят вовсе.
              const TitleTag = active ? "h1" : "h2";
              return (
                <article
                  key={slideKey(s, i)}
                  className={[
                    "hgs-story",
                    active ? "is-active" : "",
                    hasNativeDark(s, isDark) ? "hgs-story--native-dark" : "",
                  ].filter(Boolean).join(" ")}
                  aria-hidden={active ? undefined : "true"}
                >
                  <img
                    className="hgs-story__bg"
                    src={slideImage(s, isDark)}
                    alt=""
                    style={{ objectPosition: BG_POSITION[s.id] || "center" }}
                    /* первый слайд — кандидат в LCP, остальные подождут */
                    fetchpriority={i === 0 ? "high" : "low"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                  />
                  {dim > 0 && (
                    <div className="hgs-story__dim" style={{ background: `rgba(18, 18, 18, ${dim})` }} />
                  )}

                  {/* зона 1: текст */}
                  <div className="hgs-story__text">
                    <TitleTag
                      className={"hgs-story__title" + (isBigTitle(s.title) ? " hgs-story__title--big" : "")}
                      style={{ color: slideTextColor(s, isDark) }}
                    >
                      {s.title}
                    </TitleTag>
                    <p className="hgs-story__desc" style={{ color: slideTextColor(s, isDark) }}>
                      {s.description}
                    </p>
                  </div>

                  {/* зона 2: сцена — всё, что осталось между текстом и
                      панелью; предмет показывается целиком */}
                  <div className="hgs-story__stage">
                    {item && (
                      <img
                        className="hgs-story__item"
                        src={item}
                        alt=""
                        fetchpriority={i === 0 ? "high" : "low"}
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* градиент под шапкой: лого и бургер белые, а слайд бывает светлым */}
          {hero && <div className="hgs-topfade" aria-hidden="true" />}
          {/* затемнение только под панелью, поверх фона и предмета */}
          <div className="hgs-shade" aria-hidden="true" />

          {/*
            Панель общая для всех слайдов и лежит в потоке последней строкой
            сетки героя. Кнопка берётся у активного слайда: её текст и ссылка
            свои, но рисовать по кнопке на каждый слайд незачем. key по
            индексу — чтобы при смене слайда заново проигрывалось появление.
          */}
          <div className="hgs-panel" ref={panelRef}>
            <div className="hgs-panel__cta" key={currentIndex}>
              {slide.href ? (
                <a
                  href={slide.href}
                  rel="noreferrer"
                  className="hgs-panel__btn"
                  onClick={() => track("slider_cta", { slide: String(slide.title || "") })}
                >
                  {slide.text_btn}
                </a>
              ) : (
                <button
                  className="hgs-panel__btn"
                  onClick={() => {
                    track("slider_cta", { slide: String(slide.title || "") });
                    setShowModal(true);
                  }}
                >
                  {slide.text_btn}
                </button>
              )}
            </div>

            <div className="hgs-thumbs">
              {slides.map((s, i) => {
                const n = slides.length;
                let offset = i - currentIndex;
                if (offset > n / 2) offset -= n;
                if (offset < -n / 2) offset += n;
                const near = Math.abs(offset) <= 1;
                return (
                  <button
                    key={slideKey(s, i)}
                    type="button"
                    className={[
                      "hgs-thumb",
                      offset === 0 ? "is-active" : "",
                      offset === -1 ? "hgs-thumb--prev" : "",
                      offset === 1 ? "hgs-thumb--next" : "",
                    ].filter(Boolean).join(" ")}
                    aria-label={`Слайд ${i + 1}: ${s.title}`}
                    aria-current={i === currentIndex ? "true" : undefined}
                    tabIndex={near ? undefined : -1}
                    onClick={() => goToSlide(i)}
                  >
                    {/* фон слайда + предмет поверх: миниатюра читается как
                        уменьшенный кадр, а не как вырезанный объект */}
                    <img className="hgs-thumb__bg" src={slideImage(s, isDark)} alt="" loading="lazy" decoding="async" />
                    {slideItem(s, isDark) && (
                      <img className="hgs-thumb__item" src={slideItem(s, isDark)} alt="" loading="lazy" decoding="async" />
                    )}
                    {i === currentIndex && (
                      <span
                        key={runKey}
                        className="hgs-thumb__progress"
                        aria-hidden="true"
                        style={{ animationPlayState: isPaused || !isVisible ? "paused" : "running" }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="hgs-dots" aria-hidden="true">
              {slides.map((s, i) => (
                <span key={slideKey(s, i)} className={"hgs-dot" + (i === currentIndex ? " is-active" : "")} />
              ))}
            </div>
          </div>
        </section>

        {showModal && (
          <div className="hgs-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
            <div className="hgs-modal-inner">
              <Form show={showModal} setShowModal={setShowModal} id={677} endpoint="add_deal" />
            </div>
          </div>
        )}
        {debug}
      </div>
    );
  }

  return (
    <div className="hgs-root">
      <div
        className="hgs-container"
        onMouseEnter={() => { setIsPaused(true); clearInterval(intervalRef.current); elapsedRef.current = Date.now() - startTimeRef.current; }}
        onMouseLeave={() => { setIsPaused(false); }}
      >
        {/* Layered backgrounds */}
        {slides.map((s, i) => {
          const dim = darkOverlay(s, isDark);
          return (
            <div
              key={slideKey(s, i)}
              className={[
                'hgs-bg',
                i === currentIndex ? 'hgs-bg--active' : '',
                hasNativeDark(s, isDark) ? 'hgs-bg--native-dark' : '',
              ].filter(Boolean).join(' ')}
            >
              <img src={slideImage(s, isDark)} alt="" />
              {dim > 0 && (
                <div
                  className="hgs-bg__dark"
                  style={{ background: `rgba(18, 18, 18, ${dim})` }}
                />
              )}
              <div className="hgs-bg__overlay" />
            </div>
          );
        })}

        {/* Decorative grid lines */}

        {/* Content */}
        <div className="hgs-content">
          <div className="hgs-text" key={currentIndex}>
            <span className="hgs-eyebrow">{String(currentIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}</span>
            <h1
              className="hgs-title"
              style={{ color: slideTextColor(slide, isDark) }}
            >
              {slide.title}
            </h1>
            <div className="hgs-divider" style={{ background: slideTextColor(slide, isDark) }} />
            <ul className="hgs-desc">
              {slide.description.split('\n').map((line, i) => (
                <li key={i} style={{ color: slideTextColor(slide, isDark) }}>{line}</li>
              ))}
            </ul>
            <div className="hgs-cta">
              {slide.href ? (
                <a
                  href={slide.href}
                  rel="noreferrer"
                  className="hgs-btn"
                  onClick={() => track("slider_cta", { slide: String(slide.title || "") })}
                >
                  <span>{slide.text_btn}</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              ) : (
                <button
                  className="hgs-btn"
                  onClick={() => {
                    track("slider_cta", { slide: String(slide.title || "") });
                    setShowModal(true);
                  }}
                >
                  <span>{slide.text_btn}</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Image item */}
          {slideItem(slide, isDark) && (
            <div className="hgs-visual" key={`img-${currentIndex}`}>
              <img src={slideItem(slide, isDark)} alt={slide.title} />
            </div>
          )}
        </div>

        {/* Bottom bar: progress + controls */}
        <div className="hgs-bar">
          <div className="hgs-tracks">
            {slides.map((s, i) => (
              <button key={slideKey(s, i)} className="hgs-track" onClick={() => goToSlide(i)} aria-label={s.title}>
                <div
                  className="hgs-track__fill"
                  style={{
                    width: i === currentIndex
                      ? `${progress}%`
                      : i < currentIndex ? '100%' : '0%'
                  }}
                />
              </button>
            ))}
          </div>

          <div className="hgs-arrows">
            <button className="hgs-arrow" onClick={prevSlide} aria-label="Назад">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="hgs-arrow" onClick={nextSlide} aria-label="Вперёд">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7 4l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="hgs-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="hgs-modal-inner">
            <Form show={showModal} setShowModal={setShowModal} id={677} endpoint="add_deal" />
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeGeneralSlider;